import { useEffect } from "react";
import { FieldErrors } from "react-hook-form";

import type { AddDeviceFormType } from "@/apps/partner/types";

// Map of field names to their corresponding step IDs for scrolling
const FIELD_TO_STEP_MAP: Record<string, string> = {
  // Sim Selection (Step 1)
  simType: "partner-select-sim",

  // Sim Verification (Step 1 - when simType is TRIPLESIM)
  iccid: "partner-sim-verification",
  puk: "partner-sim-verification",

  // Device Selection (Step 2)
  device: "partner-device-selection", // DeviceSelection uses unique ID
  simAlias: "partner-device-selection", // Device name field

  // Rate Selection (Step 3)
  productId: "partner-rate-selection",

  // Number Porting (Step 3)
  "mnpData.mnpContractType": "partner-number-porting",
  "mnpData.currentProvider": "partner-number-porting",
  "mnpData.portedPhoneNumber": "partner-number-porting",
  "mnpData.mnpTime": "partner-number-porting",

  // Personal Details (Step 4)
  mail: "partner-personal-details",
  "customer.firstName": "first-name-input",
  "customer.lastName": "last-name-input",
  "customer.contactPhoneNumber": "phone-number-input",
  "customer.city": "city-input",
  "customer.countryCodeAlpha2": "countryCombobox",
  "customer.houseNumber": "house-number-input",
  "customer.street": "street-input",
  "customer.zip": "zip-code-input",
  "customer.salutationEnum": "partner-form-of-address",
  "customer.birthday": "birth-date-input",
  "customer.postalDeliveryNotes": "address-supplement-input",
  "customer.languageEnum": "partner-contact-language",
};

// Define the logical order of steps for prioritization
const STEP_PRIORITY_ORDER = [
  "partner-select-sim",
  "partner-sim-verification",
  "partner-device-selection",
  "partner-rate-selection",
  "partner-number-porting",
  "partner-personal-details",
  "partner-form-of-address",
  "partner-contact-language",
  "first-name-input",
  "last-name-input",
  "phone-number-input",
  "city-input",
  "countryCombobox",
  "house-number-input",
  "street-input",
  "zip-code-input",
  "birth-date-input",
  "address-supplement-input",
];

/**
 * Custom hook that automatically scrolls to the first step with validation errors
 * @param errors - Form validation errors from react-hook-form
 * @param shouldScroll - Optional flag to control when scrolling should occur
 * @param isSubmitting - Whether the form is currently being submitted
 * @param submitCount - Number of form submission attempts
 */
export const useScrollToFirstError = (
  errors: FieldErrors<AddDeviceFormType>,
  shouldScroll: boolean = true,
  isSubmitting?: boolean,
  submitCount?: number,
) => {
  useEffect(() => {
    if (!shouldScroll || !errors || Object.keys(errors).length === 0) {
      return;
    }

    // Find the first step with errors based on form flow order, not DOM position
    const findFirstErrorStep = (): string | null => {
      // Flatten nested error structure to get all field paths
      const getAllErrorFields = (errors: any): string[] => {
        const fields: string[] = [];

        // Handle top-level errors
        for (const key in errors) {
          if (errors[key] && typeof errors[key] === "object") {
            if (errors[key].message) {
              // This is an error object
              fields.push(key);
            } else {
              // This is a nested object (like customer or mnpData)
              for (const nestedKey in errors[key]) {
                if (errors[key][nestedKey]) {
                  fields.push(`${key}.${nestedKey}`);
                }
              }
            }
          }
        }

        return fields;
      };

      const errorFields = getAllErrorFields(errors);
      console.log("flattened errorFields", errorFields);

      // Get unique step IDs from error fields
      const stepIds = [
        ...new Set(
          errorFields.map((field) => FIELD_TO_STEP_MAP[field]).filter(Boolean),
        ),
      ];

      // Find the first step with errors based on logical form order
      for (const priorityStep of STEP_PRIORITY_ORDER) {
        if (stepIds.includes(priorityStep)) {
          return priorityStep;
        }
      }

      // If no step ID found, return the first field ID (for direct field scrolling)
      if (stepIds.length > 0) {
        return stepIds[0];
      }

      return null;
    };

    // Retry mechanism with multiple attempts to ensure DOM is stable
    const attemptScroll = (attempts = 0) => {
      const maxAttempts = 5;
      const firstErrorStep = findFirstErrorStep();

      if (firstErrorStep) {
        // Check if this is a step ID or a specific field ID
        const isStepId = STEP_PRIORITY_ORDER.includes(firstErrorStep);
        let targetElement = document.getElementById(firstErrorStep);

        // If it's a field ID and not found, try to find the parent step
        if (!targetElement && !isStepId) {
          // Find which step this field belongs to
          const getAllErrorFields = (errors: any): string[] => {
            const fields: string[] = [];

            // Handle top-level errors
            for (const key in errors) {
              if (errors[key] && typeof errors[key] === "object") {
                if (errors[key].message) {
                  // This is an error object
                  fields.push(key);
                } else {
                  // This is a nested object (like customer or mnpData)
                  for (const nestedKey in errors[key]) {
                    if (errors[key][nestedKey]) {
                      fields.push(`${key}.${nestedKey}`);
                    }
                  }
                }
              }
            }

            return fields;
          };

          const errorFields = getAllErrorFields(errors);
          const fieldWithError = errorFields.find(
            (field) => FIELD_TO_STEP_MAP[field] === firstErrorStep,
          );

          if (fieldWithError) {
            // Determine the parent step based on the field
            if (
              fieldWithError.startsWith("customer.") &&
              fieldWithError !== "customer.languageEnum"
            ) {
              console.log("fieldWithError", fieldWithError);
              targetElement = document.getElementById(
                "personal-details-form-fields",
              );
            } else if (fieldWithError === "mail") {
              targetElement = document.getElementById(
                "partner-personal-details",
              );
            }
          }
        }

        if (targetElement) {
          // Scroll to the element with some offset to ensure it's at the very top
          const elementRect = targetElement.getBoundingClientRect();
          const absoluteElementTop = elementRect.top + window.pageYOffset;
          const offset = 20; // Small offset to ensure element is clearly visible at top

          window.scrollTo({
            top: absoluteElementTop - offset,
            behavior: "smooth",
          });
        } else {
          if (attempts < maxAttempts) {
            // Retry with exponential backoff
            const delay = Math.min(100 * Math.pow(2, attempts), 1000);
            setTimeout(() => attemptScroll(attempts + 1), delay);
          }
        }
      } else if (attempts < maxAttempts) {
        // No step found, retry
        const delay = Math.min(100 * Math.pow(2, attempts), 1000);
        setTimeout(() => attemptScroll(attempts + 1), delay);
      }
    };

    // Start with initial attempt
    attemptScroll();
  }, [errors, shouldScroll, isSubmitting, submitCount]);
};
